/**
 * Resolve flow controllers (framework-free, so every step is unit-testable without a DOM):
 * - createResolveFlow: `auto` findings. propose (preview) -> approve (MFA step-up when the API asks) | reject ->
 *   live status (applying / applied / verifying / verified | failed) -> rollback (approve-first, step-up again).
 * - createGuideFlow: `assisted` (generated content + "I've done it") and `manual` (checklist + "mark done");
 *   both end in a single-check re-test.
 * - createInboxFlow: approvals inbox across findings; bulk approve of low-risk items behind one step-up.
 * Components subscribe with useSyncExternalStore.
 */
import type { AdvancedFinding, ProviderConnection, RemediationProposal, RemediationProviderId } from '@qm/contracts/audit-remediation';
import { actionErrorKey, isStepUpRequired, type AssistedFix, type RemediationClient, type RetestResult } from './client';

type Listener = () => void;
function store<S>(initial: S) {
  let state = initial;
  const subs = new Set<Listener>();
  return {
    get: () => state,
    set: (patch: Partial<S>) => { state = { ...state, ...patch }; subs.forEach((f) => f()); },
    subscribe: (f: Listener) => { subs.add(f); return () => { subs.delete(f); }; },
  };
}
const sleepMs = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ---------- pure helpers ----------
export type ResolveKind = 'auto' | 'connect' | 'assisted' | 'manual' | 'expert';

/** Live connection for a provider (connected first; needs_reauth / error still count as "linked but unhealthy"). */
export function connectionFor(provider: RemediationProviderId | undefined, connections: readonly ProviderConnection[]): ProviderConnection | null {
  if (!provider) return null;
  const mine = connections.filter((c) => c.provider === provider && c.status !== 'revoked');
  return mine.find((c) => c.status === 'connected') ?? mine[0] ?? null;
}

/** Which resolve experience a finding gets. `auto` without a healthy connection becomes `connect`. */
export function resolveKind(finding: Pick<AdvancedFinding, 'resolvability'>, connections: readonly ProviderConnection[]): ResolveKind {
  const r = finding.resolvability;
  if (r.mode !== 'auto') return r.mode;
  return connectionFor(r.provider, connections)?.status === 'connected' ? 'auto' : 'connect';
}

/** Proposal statuses that end a flow. `applied` is not terminal: verification follows. */
export const TERMINAL: readonly RemediationProposal['status'][] = ['verified', 'failed', 'rejected', 'expired', 'rolled_back'];
export const OPEN_PROPOSAL: readonly RemediationProposal['status'][] = ['proposed', 'approved', 'applying', 'applied', 'verified', 'failed'];

/** Customer-facing live status: approved/applying -> applying, applied -> verifying (re-test running). */
export type LiveStatus = 'applying' | 'verifying' | 'verified' | 'failed' | 'rolled_back' | 'rejected' | 'expired' | 'proposed';
export function liveStatus(p: Pick<RemediationProposal, 'status'>): LiveStatus {
  if (p.status === 'approved' || p.status === 'applying') return 'applying';
  if (p.status === 'applied') return 'verifying';
  return p.status;
}
export const LIVE_STEPS = ['applying', 'applied', 'verifying', 'verified'] as const;
/** Progress per step for the status stepper. */
export function stepStates(p: Pick<RemediationProposal, 'status'>): Record<(typeof LIVE_STEPS)[number], 'done' | 'current' | 'todo' | 'failed'> {
  const order: Record<string, number> = { approved: 0, applying: 0, applied: 2, verified: 4 };
  if (p.status === 'failed') return { applying: 'done', applied: 'failed', verifying: 'todo', verified: 'todo' };
  const at = order[p.status] ?? 0;
  const s = (i: number) => (i < at ? 'done' : i === at ? 'current' : 'todo') as 'done' | 'current' | 'todo';
  return { applying: s(0), applied: at >= 2 ? 'done' : 'todo', verifying: s(2), verified: at >= 4 ? 'done' : 'todo' };
}

export const canRollback = (p: Pick<RemediationProposal, 'reversible' | 'status'>) => p.reversible && ['applied', 'verified', 'failed'].includes(p.status);

export interface DiffLine { kind: 'add' | 'del' | 'hunk' | 'ctx'; text: string }
/** Unified diff -> typed lines (header lines dropped). */
export function parseDiff(diff: string): DiffLine[] {
  return diff.split('\n').filter((l) => !l.startsWith('+++') && !l.startsWith('---')).filter((l, i, a) => l.length || i < a.length - 1).map((l) => {
    if (l.startsWith('@@')) return { kind: 'hunk', text: l.replace(/^@@\s*|\s*@@$/g, '') };
    if (l.startsWith('+')) return { kind: 'add', text: l.slice(1) };
    if (l.startsWith('-')) return { kind: 'del', text: l.slice(1) };
    return { kind: 'ctx', text: l.startsWith(' ') ? l.slice(1) : l };
  });
}
/** Swap a diff (what a rollback restores). */
export const invertDiff = (lines: DiffLine[]): DiffLine[] => lines.map((l) => (l.kind === 'add' ? { ...l, kind: 'del' } : l.kind === 'del' ? { ...l, kind: 'add' } : l));

/** Short display value of a before/after JSON payload. */
export function showValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(showValue).join(', ');
  return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k}: ${showValue(x)}`).join('\n');
}

/** Time left until `expiresAt` (countdown). */
export function timeLeft(expiresAt: string, now: number): { expired: boolean; days: number; hours: number; minutes: number; urgent: boolean } {
  const ms = Date.parse(expiresAt) - now;
  if (!(ms > 0)) return { expired: true, days: 0, hours: 0, minutes: 0, urgent: true };
  const minutes = Math.floor(ms / 60_000);
  return { expired: false, days: Math.floor(minutes / 1440), hours: Math.floor((minutes % 1440) / 60), minutes: minutes % 60, urgent: ms < 3 * 3_600_000 };
}

/** The proposal this finding is currently on (newest open one), if any. */
export function openProposalFor(findingId: string, jobId: string, list: readonly RemediationProposal[]): RemediationProposal | null {
  return list.filter((q) => q.findingId === findingId && q.jobId === jobId && OPEN_PROPOSAL.includes(q.status))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null;
}

// ---------- auto: resolve flow ----------
export type ResolvePhase = 'idle' | 'loading' | 'preview' | 'step_up' | 'tracking' | 'rollback_confirm' | 'done' | 'error';
export interface ResolveState {
  phase: ResolvePhase;
  proposal: RemediationProposal | null;
  busy: boolean;
  /** i18n key of the last action error */
  error: string | null;
  /** what to retry after a successful step-up */
  pending: 'approve' | 'rollback' | null;
  stepUpError: string | null;
  /** polling gave up before a terminal status: show "still working" */
  stale: boolean;
  /** an approved rollback is in progress */
  rollingBack: boolean;
}
export interface ResolveDeps {
  client: RemediationClient;
  finding: Pick<AdvancedFinding, 'id' | 'resolvability'>;
  jobId: string;
  pollMs?: number;
  maxPolls?: number;
  sleep?: (ms: number) => Promise<void>;
}

export function createResolveFlow(d: ResolveDeps) {
  const s = store<ResolveState>({ phase: 'idle', proposal: null, busy: false, error: null, pending: null, stepUpError: null, stale: false, rollingBack: false });
  const sleep = d.sleep ?? sleepMs;
  let disposed = false;
  let polling = false;

  const route = (q: RemediationProposal) => {
    if (q.status === 'proposed') return s.set({ phase: 'preview', proposal: q, busy: false });
    if (TERMINAL.includes(q.status)) return s.set({ phase: 'done', proposal: q, busy: false });
    s.set({ phase: 'tracking', proposal: q, busy: false });
    void poll();
  };

  /** Poll until a terminal status; while rolling back only `rolled_back` (or a failed rollback) ends it. */
  const ended = (st: RemediationProposal['status']) => (s.get().rollingBack ? st === 'rolled_back' : TERMINAL.includes(st));
  async function poll() {
    if (polling) return;
    polling = true;
    try {
      for (let i = 0; i < (d.maxPolls ?? 60) && !disposed; i++) {
        const cur = s.get().proposal;
        if (!cur || ended(cur.status)) break;
        await sleep(d.pollMs ?? 2000);
        if (disposed) return;
        const q = await d.client.proposal(cur.id);
        const done = ended(q.status);
        s.set({ proposal: q, phase: done ? 'done' : 'tracking', stale: false, ...(done ? { rollingBack: false } : {}) });
      }
      const cur = s.get().proposal;
      if (cur && !ended(cur.status)) s.set({ stale: true });
    } catch (e) {
      s.set({ error: actionErrorKey(e) });
    } finally { polling = false; }
  }

  /** Load an open proposal for the finding, or create a preview with the connected provider. */
  async function start(connections?: readonly ProviderConnection[], fresh = false) {
    s.set({ phase: 'loading', error: null, stale: false });
    try {
      const existing = fresh ? null : openProposalFor(d.finding.id, d.jobId, await d.client.proposals());
      if (existing) return route(existing);
      const r = d.finding.resolvability;
      const con = connectionFor(r.provider, connections ?? await d.client.connections());
      if (r.mode !== 'auto' || !r.provider || !r.actionKind || !con || con.status !== 'connected') {
        return s.set({ phase: 'error', error: 'audit-remediation.actions.errors.notConnected' });
      }
      route(await d.client.propose({ jobId: d.jobId, findingId: d.finding.id, provider: r.provider, actionKind: r.actionKind, connectionId: con.id }));
    } catch (e) {
      s.set({ phase: 'error', error: actionErrorKey(e) });
    }
  }

  async function run(kind: 'approve' | 'rollback') {
    const q = s.get().proposal;
    if (!q) return;
    s.set({ busy: true, error: null });
    try {
      const next = kind === 'approve' ? await d.client.decide(q.id, { decision: 'approve' }) : await d.client.rollback(q.id);
      s.set({ pending: null, stepUpError: null, rollingBack: kind === 'rollback' && next.status !== 'rolled_back' });
      s.set({ phase: 'tracking', proposal: next, busy: false });
      if (ended(next.status)) s.set({ phase: 'done' }); else void poll();
    } catch (e) {
      if (isStepUpRequired(e)) s.set({ phase: 'step_up', pending: kind, busy: false, stepUpError: null });
      else s.set({ busy: false, error: actionErrorKey(e) });
    }
  }

  async function reject(note?: string) {
    const q = s.get().proposal;
    if (!q) return;
    s.set({ busy: true, error: null });
    try { s.set({ phase: 'done', proposal: await d.client.decide(q.id, { decision: 'reject', ...(note?.trim() ? { note: note.trim() } : {}) }), busy: false }); }
    catch (e) { s.set({ busy: false, error: actionErrorKey(e) }); }
  }

  async function submitStepUp(code: string) {
    const pending = s.get().pending;
    if (!pending) return;
    if (!/^\d{6}$/.test(code.trim())) return s.set({ stepUpError: 'audit-remediation.actions.stepUp.format' });
    s.set({ busy: true, stepUpError: null });
    try { await d.client.stepUp(code.trim()); }
    catch { return s.set({ busy: false, stepUpError: 'audit-remediation.actions.stepUp.invalid' }); }
    await run(pending);
  }

  return {
    getState: s.get, subscribe: s.subscribe, start, reject, submitStepUp,
    /** After a failed apply the old proposal stays open (rollback); "propose again" starts a fresh one. */
    retry: (connections?: readonly ProviderConnection[]) => start(connections, s.get().proposal?.status === 'failed'),
    approve: () => run('approve'),
    cancelStepUp: () => { const q = s.get().proposal; s.set({ pending: null, stepUpError: null, busy: false, phase: q?.status === 'proposed' ? 'preview' : q ? (TERMINAL.includes(q.status) ? 'done' : 'tracking') : 'idle' }); },
    askRollback: () => { const q = s.get().proposal; if (q && canRollback(q)) s.set({ phase: 'rollback_confirm', error: null }); },
    cancelRollback: () => { const q = s.get().proposal; if (q) route(q); },
    confirmRollback: () => run('rollback'),
    dispose: () => { disposed = true; },
  };
}
export type ResolveFlow = ReturnType<typeof createResolveFlow>;

// ---------- assisted + manual: guide flow ----------
export interface GuideState {
  phase: 'idle' | 'loading' | 'ready' | 'error';
  assist: AssistedFix | null;
  checked: readonly number[];
  retest: RetestResult | null;
  busy: boolean;
  error: string | null;
}
export function createGuideFlow(d: { client: RemediationClient; finding: Pick<AdvancedFinding, 'id' | 'resolvability' | 'steps'>; jobId: string }) {
  const s = store<GuideState>({ phase: 'idle', assist: null, checked: [], retest: null, busy: false, error: null });
  return {
    getState: s.get, subscribe: s.subscribe,
    async start() {
      if (d.finding.resolvability.mode !== 'assisted') return s.set({ phase: 'ready' });
      s.set({ phase: 'loading', error: null });
      try { s.set({ phase: 'ready', assist: await d.client.assist(d.jobId, d.finding.id) }); }
      catch (e) { s.set({ phase: 'error', error: actionErrorKey(e) }); }
    },
    toggle(i: number) {
      const c = s.get().checked;
      s.set({ checked: c.includes(i) ? c.filter((x) => x !== i) : [...c, i].sort((a, b) => a - b) });
    },
    /** manual needs every step ticked; assisted is a single confirmation. */
    canFinish(): boolean { return d.finding.resolvability.mode !== 'manual' || s.get().checked.length === d.finding.steps.length; },
    async done() {
      if (!this.canFinish()) return s.set({ error: 'audit-remediation.actions.manual.incomplete' });
      s.set({ busy: true, error: null });
      try { s.set({ retest: await d.client.retest(d.jobId, d.finding.id), busy: false }); }
      catch (e) { s.set({ busy: false, error: actionErrorKey(e) }); }
    },
  };
}
export type GuideFlow = ReturnType<typeof createGuideFlow>;

// ---------- approvals inbox ----------
export interface InboxState {
  phase: 'loading' | 'ready' | 'error';
  failure: unknown;
  items: RemediationProposal[];
  selected: readonly string[];
  busy: boolean;
  error: string | null;
  /** action waiting on a step-up */
  pending: { kind: 'bulk'; ids: string[] } | { kind: 'one'; id: string } | null;
  stepUpError: string | null;
  /** ids approved in the last action (announced) */
  approved: number;
}
export const isPending = (q: RemediationProposal, now: number) => q.status === 'proposed' && !timeLeft(q.expiresAt, now).expired;
export const bulkEligible = (q: RemediationProposal, now: number) => isPending(q, now) && q.risk === 'low';

export function createInboxFlow(d: { client: RemediationClient; now?: () => number }) {
  const now = d.now ?? Date.now;
  const s = store<InboxState>({ phase: 'loading', failure: null, items: [], selected: [], busy: false, error: null, pending: null, stepUpError: null, approved: 0 });
  const merge = (list: RemediationProposal[]) => {
    const byId = new Map(list.map((q) => [q.id, q]));
    s.set({ items: s.get().items.map((q) => byId.get(q.id) ?? q) });
  };
  async function exec(p: NonNullable<InboxState['pending']>) {
    s.set({ busy: true, error: null, approved: 0 });
    try {
      const out = p.kind === 'bulk' ? await d.client.bulkApprove(p.ids) : [await d.client.decide(p.id, { decision: 'approve' })];
      merge(out);
      s.set({ busy: false, pending: null, stepUpError: null, selected: [], approved: out.length });
    } catch (e) {
      if (isStepUpRequired(e)) s.set({ busy: false, pending: p, stepUpError: null });
      else s.set({ busy: false, error: actionErrorKey(e) });
    }
  }
  return {
    getState: s.get, subscribe: s.subscribe,
    async load() {
      try {
        const items = [...await d.client.proposals()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        s.set({ phase: 'ready', items, failure: null });
      } catch (e) { s.set({ phase: 'error', failure: e }); }
    },
    pendingItems: () => s.get().items.filter((q) => isPending(q, now())),
    historyItems: () => s.get().items.filter((q) => !isPending(q, now())),
    toggle(id: string) {
      const q = s.get().items.find((x) => x.id === id);
      if (!q || !bulkEligible(q, now())) return;
      const sel = s.get().selected;
      s.set({ selected: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
    },
    selectAllLowRisk() { s.set({ selected: s.get().items.filter((q) => bulkEligible(q, now())).map((q) => q.id) }); },
    clearSelection() { s.set({ selected: [] }); },
    bulkApprove() { const ids = s.get().selected.filter((id) => s.get().items.some((q) => q.id === id && bulkEligible(q, now()))); return ids.length ? exec({ kind: 'bulk', ids }) : Promise.resolve(); },
    approve(id: string) { return exec({ kind: 'one', id }); },
    async reject(id: string) {
      s.set({ busy: true, error: null });
      try { merge([await d.client.decide(id, { decision: 'reject' })]); s.set({ busy: false, selected: s.get().selected.filter((x) => x !== id) }); }
      catch (e) { s.set({ busy: false, error: actionErrorKey(e) }); }
    },
    async submitStepUp(code: string) {
      const p = s.get().pending;
      if (!p) return;
      if (!/^\d{6}$/.test(code.trim())) return s.set({ stepUpError: 'audit-remediation.actions.stepUp.format' });
      s.set({ busy: true, stepUpError: null });
      try { await d.client.stepUp(code.trim()); } catch { return s.set({ busy: false, stepUpError: 'audit-remediation.actions.stepUp.invalid' }); }
      await exec(p);
    },
    cancelStepUp() { s.set({ pending: null, stepUpError: null, busy: false }); },
  };
}
export type InboxFlow = ReturnType<typeof createInboxFlow>;
